import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppContext } from './useAppContext';
import { reset } from '../context/actions';
import { oktaAuthClient } from '../utils/oktaAuthClient';
import { initialState } from '../context';
import { Routes } from '../types';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_THRESHOLD = 60 * 1000; // 1 minute

export interface UseInactivityTimeoutReturn {
  showWarning: boolean;
  secondsRemaining: number;
  handleContinueSession: () => void;
  handleExitSession: () => void;
}

interface UseInactivityTimerProps {
  enable?: boolean;
}

export const useInactivityTimer = ({ enable = true }: UseInactivityTimerProps = {}): UseInactivityTimeoutReturn => {
  const { dispatch, updateState, state } = useAppContext();
  const history = useHistory();
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [isExpired, setIsExpired] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const resetActivity = useCallback(() => {
    setLastActivityTime(Date.now());
    setShowWarning(false);
  }, []);

    const handleSessionExpired = useCallback(() => {
    oktaAuthClient.tokenManager.clear();
    oktaAuthClient.closeSession();

    sessionStorage.clear();

    dispatch(reset());
    const { flow, operation, source, countryCode } = state
    updateState({
      ...initialState,
      flow,
      operation,
      source,
      countryCode,
    });

    history.replace(Routes.sessionExpired);

    setIsExpired(true);
    setShowWarning(false);
  }, [dispatch, updateState, history, state]);

  const handleContinueSession = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  const handleExitSession = useCallback(() => {
    handleSessionExpired();
  }, [handleSessionExpired]);

  // Monitor user activity
  useEffect(() => {
    if (!enable) return;

    const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    const handleActivity = () => {
      if (!isExpired && !showWarning) {
        resetActivity();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [resetActivity, isExpired, showWarning, enable]);

  // Timer to check inactivity
  useEffect(() => {
    if (isExpired || !enable) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTime;
      const timeRemaining = INACTIVITY_TIMEOUT - timeSinceLastActivity;

      if (timeRemaining <= 0) {
        handleSessionExpired();
      } else if (timeRemaining <= WARNING_THRESHOLD) {
        setShowWarning(true);
        setSecondsRemaining(Math.ceil(timeRemaining / 1000));
      } else {
        setShowWarning(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivityTime, isExpired, handleSessionExpired, enable]);



  return {
    showWarning: enable ? showWarning : false,
    secondsRemaining,
    handleContinueSession,
    handleExitSession,
  };
};
